// SGEW 28 Oct 2026 (owner: "calculate the weight of round, pipe, angle, channel... like the flat bar"): an item's or
// sub-item's weight from its shape, material and sizes - weight = cross-section area x length x density, the density
// coming from the material alone (mild steel 7.85 g/cm3 whether flat, round or pipe). Sizes in mm, weight per piece of
// the length (SGEW sets bar stock up per foot, 304.8 mm). The server has the same formulas
// (sgew_backend/app/Libraries/ShapeWeight.php) - keep the two the same.

export const SHAPES = [
  { value: "flat", label: "Flat / Plate / Square bar" },
  { value: "round", label: "Round bar / Shaft" },
  { value: "hex", label: "Hexagon bar" },
  { value: "pipe", label: "Round pipe" },
  { value: "box", label: "Square / Rectangular pipe" },
  { value: "angle", label: "Angle" },
  { value: "channel", label: "Channel" },
  { value: "disc", label: "Disc / Ring" },
  { value: "bought", label: "Bought-out piece" },
  { value: "nosize", label: "No size (consumable)" },
];

// density in g/cm3; Other takes the typed one
export const MATERIALS = [
  { value: "mild_steel", label: "Mild steel / Iron", density: 7.85 },
  { value: "alloy_steel", label: "Spring / alloy steel (EN8, EN9, EN45, boron)", density: 7.85 },
  { value: "cast_iron", label: "Cast iron", density: 7.2 },
  { value: "sg_iron", label: "SG / ductile iron", density: 7.1 },
  { value: "stainless", label: "Stainless steel", density: 7.93 },
  { value: "aluminium", label: "Aluminium", density: 2.7 },
  { value: "brass", label: "Brass", density: 8.5 },
  { value: "bronze", label: "Bronze / Gun metal", density: 8.8 },
  { value: "copper", label: "Copper", density: 8.96 },
  { value: "nylon", label: "Nylon / plastic", density: 1.15 },
  { value: "rubber", label: "Rubber", density: 1.2 },
  { value: "other", label: "Other (type the density)", density: null },
];

// the sizes each shape needs for its weight (a disc has no length; its hole is optional)
export const NEEDS = {
  flat: ["length", "width", "thickness"],
  round: ["length", "diameter"],
  hex: ["length", "diameter"],
  pipe: ["length", "diameter", "thickness"],
  box: ["length", "width", "height", "thickness"],
  angle: ["length", "width", "height", "thickness"],
  channel: ["length", "height", "width", "thickness"],
  disc: ["diameter", "thickness"],
  bought: [],
  nosize: [],
};

// the sizes a shape shows on a form: [size, label]
export const FIELDS = {
  flat: [["length", "Length"], ["width", "Width"], ["thickness", "Thickness"]],
  round: [["length", "Length"], ["diameter", "Diameter"]],
  hex: [["length", "Length"], ["diameter", "Across flats"]],
  pipe: [["length", "Length"], ["diameter", "Outer diameter"], ["thickness", "Wall thickness"]],
  box: [["length", "Length"], ["width", "Width"], ["height", "Height"], ["thickness", "Wall thickness"]],
  angle: [["length", "Length"], ["width", "Leg A"], ["height", "Leg B"], ["thickness", "Thickness"]],
  channel: [["length", "Length"], ["height", "Height (web)"], ["width", "Flange width"], ["thickness", "Thickness"]],
  disc: [["diameter", "Outer diameter"], ["hole", "Hole (0 = solid)"], ["thickness", "Thickness"]],
  bought: [],
  nosize: [],
};

// the form / table field of each size
export const COLUMNS = {
  length: "default_length_mm",
  width: "default_width_mm",
  thickness: "default_thickness_mm",
  diameter: "default_diameter_mm",
  height: "default_height_mm",
  hole: "default_hole_mm",
};

export const shapeOf = (v) => (NEEDS[String(v || "").toLowerCase()] ? String(v).toLowerCase() : "flat");
export const shapeLabel = (v) => (SHAPES.find((s) => s.value === shapeOf(v)) || SHAPES[0]).label;
export const materialLabel = (v) => (MATERIALS.find((m) => m.value === v) || MATERIALS[0]).label;
export const hasFormula = (shape) => NEEDS[shapeOf(shape)].length > 0;

// the density (g/cm3) of a material; Other takes the typed one
export const densityOf = (material, typed) => {
  const m = MATERIALS.find((x) => x.value === material);
  if (m && m.density !== null) return m.density;
  const n = parseFloat(typed);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

// sizes of a record or form values (default_*_mm, or plain keys length / width / ...)
export const sizesOf = (row = {}) =>
  Object.fromEntries(Object.entries(COLUMNS).map(([k, col]) => [k, num(row[col] ?? row[k])]));

// cross-section area in mm2 (a disc's face); null when a size is missing or the shape has no formula
export const area = (shape, s) => {
  const need = NEEDS[shape] || [];
  if (!need.length || need.some((k) => k !== "length" && !(s[k] > 0))) return null;
  const { width: w = 0, thickness: t = 0, diameter: d = 0, height: h = 0 } = s;
  const hole = Math.max(0, s.hole || 0);
  switch (shape) {
    case "flat":
      return w * t;
    case "round":
      return (Math.PI / 4) * d * d;
    case "hex":
      return (Math.sqrt(3) / 2) * d * d;
    case "pipe":
      return (Math.PI / 4) * (d * d - (d - 2 * t) ** 2);
    case "box":
      return 2 * t * (w + h - 2 * t);
    case "angle":
      return t * (w + h - t);
    case "channel":
      return t * (h + 2 * w - 2 * t);
    case "disc":
      return (Math.PI / 4) * (d * d - hole * hole);
    default:
      return null;
  }
};

const round2 = (n) => Math.round(n * 100) / 100;

// kg per metre of a bar shape (null for disc / bought / nosize or when something is missing)
export const kgPerMetre = (shape, s, density) => {
  if (shape === "disc" || density === null) return null;
  const a = area(shape, s);
  return a === null ? null : (a * density) / 1000;
};

// the weight of one piece in kg, 2 decimals (null when it cannot be worked out)
export const pieceKg = (shape, s, density) => {
  if (density === null) return null;
  const a = area(shape, s);
  if (a === null) return null;
  if (shape === "disc") return round2((a * s.thickness * density) / 1e6);
  return s.length > 0 ? round2((a * s.length * density) / 1e6) : null;
};

// what is wrong with the sizes of a shape (a wall too thick for its pipe ...), or null
export const sizeError = (shape, s) => {
  const neg = Object.entries(s).find(([, v]) => v !== null && v < 0);
  if (neg) return `${neg[0][0].toUpperCase()}${neg[0].slice(1)} cannot be below 0.`;
  const { width: w = 0, thickness: t = 0, diameter: d = 0, height: h = 0, hole = 0 } = s;
  if (shape === "pipe" && d > 0 && t > 0 && 2 * t >= d) return `The wall (${t} mm) must be less than half the outer diameter (${d} mm).`;
  if (shape === "box" && w > 0 && h > 0 && t > 0 && 2 * t >= Math.min(w, h)) return `The wall (${t} mm) must be less than half the width and the height.`;
  if (shape === "angle" && w > 0 && h > 0 && t > 0 && t >= Math.min(w, h)) return `The thickness (${t} mm) must be less than both legs.`;
  if (shape === "channel" && w > 0 && h > 0 && t > 0 && (2 * t >= h || t >= w)) return `The thickness (${t} mm) must be less than the flange and half the height.`;
  if (shape === "disc" && d > 0 && hole > 0 && hole >= d) return `The hole (${hole} mm) must be smaller than the outer diameter (${d} mm).`;
  return null;
};

// a record's figures: kg per metre and its worked-out piece weight (both null when not computable)
export const figuresOf = (row = {}) => {
  const shape = shapeOf(row.shape);
  const density = densityOf(row.material || "mild_steel", row.density);
  const s = sizesOf(row);
  return { shape, density, kgPerM: kgPerMetre(shape, s, density), pieceKg: pieceKg(shape, s, density) };
};

// "Round bar / Shaft 32 x 304.8 mm" - the sizes in the shape's own words, for views and lists
export const sizeText = (row = {}) => {
  const shape = shapeOf(row.shape);
  const s = sizesOf(row);
  const show = (FIELDS[shape] || []).filter(([k]) => s[k] !== null && !(k === "hole" && !s[k]));
  if (!show.length) return shapeLabel(shape);
  return `${shapeLabel(shape)}: ${show.map(([k, l]) => `${l} ${s[k]}`).join(", ")} mm`;
};

// the weight of a piece of a record cut to another length (purchase line, split line); flat lines may change width /
// thickness too. Null when not computable (then the typed weight stays).
export const lineKg = (row, line = {}) => {
  const shape = shapeOf(row.shape);
  const density = densityOf(row.material || "mild_steel", row.density);
  const s = { ...sizesOf(row) };
  if (line.length !== undefined && line.length !== "") s.length = num(line.length);
  if (shape === "flat") {
    if (line.width !== undefined && line.width !== "") s.width = num(line.width);
    if (line.thickness !== undefined && line.thickness !== "") s.thickness = num(line.thickness);
  }
  return pieceKg(shape, s, density);
};

// the section of a split line that carries its sub-item's shape (splits from 28 Oct: shape, diameter, height, hole with
// the line's width / thickness): "Ø 63", "L 50 × 50 × 6" ...; null for a flat piece or an older line (then its width and
// thickness are shown)
export const splitLineSection = (line = {}) => {
  const sh = shapeOf(line.shape);
  if (!line.shape || sh === "flat") return null;
  return sectionShort({ shape: sh, width: line.width, thickness: line.thickness, diameter: line.diameter, height: line.height, hole: line.hole });
};

// a short section for a list: "50 × 10", "Ø 32", "Hex 24", "Ø 48.3 × 3.2 wall", "□ 50 × 50 × 3 wall", "L 50 × 50 × 6",
// "C 100 × 50 × 6", "Disc Ø 460, hole 40 × 4", "Bought-out", "No size"
export const sectionShort = (row = {}) => {
  const sh = shapeOf(row.shape);
  const s = sizesOf(row);
  const n = (v) => (v === null ? "?" : String(v));
  switch (sh) {
    case "flat":
      return s.width === null && s.thickness === null ? "-" : `${n(s.width)} × ${n(s.thickness)}`;
    case "round":
      return `Ø ${n(s.diameter)}`;
    case "hex":
      return `Hex ${n(s.diameter)}`;
    case "pipe":
      return `Ø ${n(s.diameter)} × ${n(s.thickness)} wall`;
    case "box":
      return `□ ${n(s.width)} × ${n(s.height)} × ${n(s.thickness)} wall`;
    case "angle":
      return `L ${n(s.width)} × ${n(s.height)} × ${n(s.thickness)}`;
    case "channel":
      return `C ${n(s.height)} × ${n(s.width)} × ${n(s.thickness)}`;
    case "disc":
      return `Disc Ø ${n(s.diameter)}${s.hole ? `, hole ${s.hole}` : ""} × ${n(s.thickness)}`;
    case "bought":
      return "Bought-out";
    default:
      return "No size";
  }
};
